import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// `withCredentials: true` ensures the httpOnly `access_token` cookie set by
// /api/auth/login is automatically included on every subsequent request.
export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Backwards-compat: also attach JWT from local/session storage if present.
// The backend accepts either transport (cookie preferred). We check both
// stores so "Remember me = off" sessions (sessionStorage) work identically
// to persistent ones (localStorage), and we transparently migrate any legacy
// `of_token` on first read so existing sessions aren't dropped when this
// ships.
api.interceptors.request.use((cfg) => {
  let token = localStorage.getItem("sres_token") || sessionStorage.getItem("sres_token");
  if (!token) {
    const legacy = localStorage.getItem("of_token") || sessionStorage.getItem("of_token");
    if (legacy) {
      localStorage.setItem("sres_token", legacy);
      localStorage.removeItem("of_token");
      sessionStorage.removeItem("of_token");
      token = legacy;
    }
  }
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Global 401 handler: clear token so ProtectedRoute redirects
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("sres_token");
      localStorage.removeItem("of_token");
      sessionStorage.removeItem("sres_token");
      sessionStorage.removeItem("of_token");
      if (!location.pathname.startsWith("/login") && !location.pathname.startsWith("/signup")) {
        location.replace("/login?expired=1");
      }
    }
    return Promise.reject(err);
  }
);

// ---- Auth ----
export const signup = (body) => api.post("/auth/signup", body).then((r) => r.data);
export const login = (body) => api.post("/auth/login", body).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const me = () => api.get("/auth/me").then((r) => r.data);
export const changePassword = (body) => api.post("/auth/change-password", body).then((r) => r.data);
export const updatePreferences = (body) => api.patch("/auth/preferences", body).then((r) => r.data);

// ---- Admin ----
export const adminListUsers = (status = null) =>
  api.get("/admin/users", { params: status ? { status } : {} }).then((r) => r.data);
export const adminPending = () => api.get("/admin/users/pending").then((r) => r.data);
export const adminApprove = (uid, body) => api.post(`/admin/users/${uid}/approve`, body).then((r) => r.data);
export const adminReject = (uid) => api.post(`/admin/users/${uid}/reject`).then((r) => r.data);
export const adminUpdateValidity = (uid, body) => api.patch(`/admin/users/${uid}/validity`, body).then((r) => r.data);
export const adminUpdateRole = (uid, role) => api.patch(`/admin/users/${uid}/role`, null, { params: { role } }).then((r) => r.data);
export const adminAuditLog = (limit = 200) => api.get("/admin/audit-log", { params: { limit } }).then((r) => r.data);
export const adminCreateUser = (body) => api.post("/admin/users", body).then((r) => r.data);

// ---- Company-scoped admin (role=admin) ----
export const companyScope        = (company) => api.get("/company-admin/scope", { params: company ? { company } : {} }).then((r) => r.data);
export const companyListUsers    = (company) => api.get("/company-admin/users", { params: company ? { company } : {} }).then((r) => r.data);
export const companyInviteUser   = (body) => api.post("/company-admin/users/invite", body).then((r) => r.data);
export const companyRevokeUser   = (uid) => api.post(`/company-admin/users/${uid}/revoke`).then((r) => r.data);
export const companyUpdateValidity = (uid, body) => api.patch(`/company-admin/users/${uid}/validity`, body).then((r) => r.data);
export const auditExportUrl = (fromDate, toDate, company) => {
  const p = new URLSearchParams();
  if (fromDate) p.set("from", fromDate);
  if (toDate)   p.set("to", toDate);
  if (company)  p.set("company", company);
  return `${API_BASE}/company-admin/audit-export.csv${p.toString() ? "?" + p.toString() : ""}`;
};

// ---- Compliance email (monthly Resend send) ----
export const complianceStatus  = () => api.get("/admin/compliance-email/status").then((r) => r.data);
export const complianceSendNow = () => api.post("/admin/compliance-email/send-now").then((r) => r.data);
export const complianceGetConfig = () => api.get("/admin/compliance-email/config").then((r) => r.data);
export const compliancePutConfig = (body) => api.put("/admin/compliance-email/config", body).then((r) => r.data);

// ---- Public domain (URL embedded in outbound emails & PDFs) ----
export const adminGetDomain = () => api.get("/admin/domain").then((r) => r.data);
export const adminPutDomain = (url) => api.put("/admin/domain", { url }).then((r) => r.data);

// ---- Projects ----
export const listProjects = (opts = {}) =>
  api.get("/projects", { params: opts.includeArchived ? { include_archived: true } : {} })
    .then((r) => r.data);

// Iter90 · Feb 2026 · lightweight counts endpoint for the Portfolio
// `View archived · N` chip. Returns {total, archived, active}.
export const getProjectCounts = () =>
  api.get("/projects/counts").then((r) => r.data);

// Iter94 · Feb 2026 · lifecycle events for a project (archive/restore
// timeline) and portfolio-wide 90-day analytics.
export const getProjectArchiveEvents = (pid) =>
  api.get(`/projects/${pid}/archive-events`).then((r) => r.data);

export const getProjectsAnalytics = (days = 90) =>
  api.get(`/projects/analytics/${days}d`).then((r) => r.data);
export const createProject = (body) => api.post("/projects", body).then((r) => r.data);
export const getProject = (id) => api.get(`/projects/${id}`).then((r) => r.data);
export const updateProject = (id, body) => api.patch(`/projects/${id}`, body).then((r) => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then((r) => r.data);
export const getStats = (id) => api.get(`/projects/${id}/stats`).then((r) => r.data);

// ---- Workspace persona (Intelligent | Professional | Combined) ----
export const setProjectWorkspace = (id, workspace) =>
  api.put(`/projects/${id}/workspace`, { workspace }).then((r) => r.data);
export const setProjectModuleWorkspace = (id, module, workspace) =>
  api.put(`/projects/${id}/workspace/module`, { module, workspace })
      .then((r) => r.data);
export const getWorkspaceAudit = (id, limit = 50) =>
  api.get(`/projects/${id}/workspace/audit`, { params: { limit } })
      .then((r) => r.data);

// ---- Uploads ----
export const uploadCSV = (id, kind, file, mapping = null) => {
  const fd = new FormData();
  fd.append("file", file);
  if (mapping) fd.append("mapping", JSON.stringify(mapping));
  return api.post(`/projects/${id}/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const previewData = (id, kind, limit = 100) => api.get(`/projects/${id}/data/${kind}?limit=${limit}`).then((r) => r.data);

// ---- Sprint P5k · Data Import (Module 3) — multi-file + auto-generate ----
export const dataImportMultiUpload = (pid, files) => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return api.post(`/projects/${pid}/data-import/multi-upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const dataImportSummary = (pid) =>
  api.get(`/projects/${pid}/data-import/summary`).then((r) => r.data);
export const dataImportAutoGenerate = (pid) =>
  api.post(`/projects/${pid}/data-import/auto-generate`).then((r) => r.data);
export const dataImportApprove = (pid) =>
  api.post(`/projects/${pid}/data-import/approve`).then((r) => r.data);

// ---- Sprint P5l · Maps & GIS (Module 4) — wizard summary + state + approve ----
export const gisSummary = (pid) =>
  api.get(`/projects/${pid}/gis/summary`).then((r) => r.data);
export const getGisWizardState = (pid) =>
  api.get(`/projects/${pid}/gis/wizard-state`).then((r) => r.data);
export const putGisWizardState = (pid, body) =>
  api.put(`/projects/${pid}/gis/wizard-state`, body).then((r) => r.data);
export const gisApprove = (pid) =>
  api.post(`/projects/${pid}/gis/approve`).then((r) => r.data);
export const gisGenerateWorkspace = (pid) =>
  api.post(`/projects/${pid}/gis/generate-workspace`).then((r) => r.data);

// ---- Sprint P5n · Project Setup — one-click AUTO DETECT PROJECT SETTINGS ----
export const setupAutoDetect = (pid, apply = false) =>
  api.post(`/projects/${pid}/setup/auto-detect?apply=${apply ? "true" : "false"}`).then((r) => r.data);

// ---- Sprint P5m · Geology Module 7 — auto-gen maps + sections + 3D wireframes ----
export const geologySummary = (pid) =>
  api.get(`/projects/${pid}/geology/summary`).then((r) => r.data);
export const geologyAutoDetectCodes = (pid) =>
  api.post(`/projects/${pid}/geology/auto-detect-codes`).then((r) => r.data);
export const geologySuggestDomains = (pid) =>
  api.post(`/projects/${pid}/geology/suggest-domains`).then((r) => r.data);
export const geologyGetState = (pid) =>
  api.get(`/projects/${pid}/geology/state`).then((r) => r.data);
export const geologyPutState = (pid, body) =>
  api.put(`/projects/${pid}/geology/state`, body).then((r) => r.data);
export const geologyGenerate2DMap = (pid, body = {}) =>
  api.post(`/projects/${pid}/geology/generate-2d-map`, body).then((r) => r.data);
export const geologyGenerateSections = (pid, body = {}) =>
  api.post(`/projects/${pid}/geology/generate-sections`, body).then((r) => r.data);
export const geologyGenerate3DModel = (pid, body = {}) =>
  api.post(`/projects/${pid}/geology/generate-3d-model`, body).then((r) => r.data);
export const geologyValidateGeometry = (pid) =>
  api.post(`/projects/${pid}/geology/validate-geometry`).then((r) => r.data);
export const geologyApprove = (pid) =>
  api.post(`/projects/${pid}/geology/approve`).then((r) => r.data);
export const geologyGetModel = (pid) =>
  api.get(`/projects/${pid}/geology/model`).then((r) => r.data);

// ---- Density ----
export const getDensity = (id) => api.get(`/projects/${id}/density`).then((r) => r.data);
export const saveDensity = (id, body) => api.post(`/projects/${id}/density`, body).then((r) => r.data);

// ---- 3D / Section / Variogram ----
export const getDrillholes3D = (id, element = null) =>
  api.get(`/projects/${id}/drillholes3d`, { params: element ? { element } : {} }).then((r) => r.data);
export const getSectionBounds = (id) => api.get(`/projects/${id}/section/bounds`).then((r) => r.data);
export const getSection = (id, body) => api.post(`/projects/${id}/section`, body).then((r) => r.data);
export const runVariogram = (id, body) => api.post(`/projects/${id}/variogram`, body).then((r) => r.data);

// ---- Implicit modelling — RBF ore-shell ----
export const buildOreShell = (id, params) =>
  api.post(`/projects/${id}/implicit/build`, params).then((r) => r.data);
export const getOreShell = (id) => api.get(`/projects/${id}/implicit`).then((r) => r.data);
export const deleteOreShell = (id) => api.delete(`/projects/${id}/implicit`).then((r) => r.data);

// ---- Block model / Job / Pit / AI ----
/**
 * Kick off a block-model build. Transparently handles both sync and async
 * backend responses:
 *   • Sync (IDW / small OK)   → response = full block-model doc; returned as-is.
 *   • Async (OK on > 3.5 k blocks) → response = { job_id, status: "queued" };
 *     this helper polls `/api/jobs/{job_id}` every `pollMs` ms and resolves
 *     with the finished block-model doc (or rejects on error).
 *
 * Consumers can subscribe to intermediate progress via `onProgress` callback
 * which receives `{ status, progress, message }` on every poll.
 */
/**
 * P0 fix (Feb 2026) — extract a human-readable, actionable message from
 * either a job-error payload or a raw axios error so the UI never has to
 * show a naked "Request failed with status code 400" ever again.
 */
function _jobError(jobDoc) {
  const detail = jobDoc?.error_detail;
  if (detail && typeof detail === "object") {
    const err = new Error(detail.message || jobDoc.error || "Estimation failed");
    // Fabricate an axios-like response so BackendErrorPanel can render it
    // with the same field-level detail as a synchronous 400.
    err.response = { data: { detail }, status: 400 };
    return err;
  }
  return new Error(jobDoc?.error || "Estimation failed");
}

export async function buildBlockModel(id, params, {
  pollMs = 1500, timeoutMs = 15 * 60 * 1000, onProgress,
} = {}) {
  const res = await api.post(`/projects/${id}/block_model`, params);
  const data = res.data;
  // Sync branch — return immediately.
  if (!data?.job_id) return data;

  // Async branch — poll until done / error / timeout.
  const jobId = data.job_id;
  onProgress?.({ status: "queued", progress: 0, message: data.message || "" });
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollMs));
    let jobDoc;
    try {
      jobDoc = (await api.get(`/jobs/${jobId}`)).data;
    } catch (e) {
      throw new Error(`Job polling failed: ${e?.message || e}`);
    }
    onProgress?.({
      status: jobDoc.status,
      progress: jobDoc.progress ?? 0,
      message: jobDoc.message || "",
    });
    if (jobDoc.status === "done") {
      // Fetch the persisted block model — the job doc only carries summary.
      return await getBlockModel(id);
    }
    if (jobDoc.status === "error") {
      throw _jobError(jobDoc);
    }
  }
  throw new Error("Block-model build timed out (>15 min)");
}
export const getBlockModel = (id, units = "metric") => api.get(`/projects/${id}/block_model`, { params: { units } }).then((r) => r.data);
// P12 · Export Center per-artifact metadata (size · updated timestamp).
export const getExportsSummary = (id) =>
  api.get(`/projects/${id}/exports/summary`).then((r) => r.data);
// P4 — Drill-gap / anti-artificial-continuity heat-map for the block model.
export const getBlockModelGapMap = (id, sample_step = 1) =>
  api.get(`/projects/${id}/block-model/gap-map`, { params: { sample_step } })
    .then((r) => r.data);
// P5 — Wizard Step 8 (Composite Creation) + Step 9 (Top-Cut Analysis).
// `compositePreviewFull` returns rich raw-vs-composited stats + histograms
// distinct from the older `compositePreview` endpoint below.
export const compositePreviewFull = (id, body) =>
  api.post(`/projects/${id}/composite/preview`, body).then((r) => r.data);
export const topCutAnalyze = (id, body) =>
  api.post(`/projects/${id}/topcut/analyze`, body).then((r) => r.data);
// P5b — Wizard Step 6 (Geological Domains) + Step 17 (Resource Validation).
export const wizardDomainStats = (id, { field = "lithology", element } = {}) =>
  api.get(`/projects/${id}/wizard/domain-stats`,
    { params: { field, element } }).then((r) => r.data);
export const wizardSwath = (id, { element, n_bins = 20 } = {}) =>
  api.get(`/projects/${id}/wizard/swath`,
    { params: { element, n_bins } }).then((r) => r.data);

// P5d — Wizard Persistent State + Detection + Presets + CPR PDF.
export const getWizardState = (pid) =>
  api.get(`/projects/${pid}/wizard/state`).then((r) => r.data);
export const patchWizardState = (pid, body) =>
  api.put(`/projects/${pid}/wizard/state`, body).then((r) => r.data);
export const saveWizardStep = (pid, stepId, body) =>
  api.put(`/projects/${pid}/wizard/state/${stepId}`, body).then((r) => r.data);
export const approveWizardStep = (pid, stepId, reason = "") =>
  api.post(`/projects/${pid}/wizard/state/${stepId}/approve`, { reason })
    .then((r) => r.data);
export const resetWizardStep = (pid, stepId) =>
  api.post(`/projects/${pid}/wizard/state/${stepId}/reset`).then((r) => r.data);
export const wizardStepHistory = (pid, stepId) =>
  api.get(`/projects/${pid}/wizard/state/${stepId}/history`).then((r) => r.data);
// Sprint P13 wrap-up · wizard step-transition analytics + resume helper.
export const recordWizardStepTransition = (pid, body) =>
  api.post(`/projects/${pid}/wizard/step-transition`, body).then((r) => r.data);
export const listWizardStepTransitions = (pid, limit = 100) =>
  api.get(`/projects/${pid}/wizard/step-transitions`, { params: { limit } })
    .then((r) => r.data);
export const getWizardStepProgress = (pid) =>
  api.get(`/projects/${pid}/wizard/step-progress`).then((r) => r.data);

// Sprint P14 · Professional CPR helpers.
export const warmUpProCprNarratives = (pid) =>
  api.post(`/projects/${pid}/export/pro-cpr/warm-up`).then((r) => r.data);
export const getProCprPreview = (pid) =>
  api.get(`/projects/${pid}/export/pro-cpr/preview`).then((r) => r.data);


export const detectProject = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/project`).then((r) => r.data);
export const detectData = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/data`).then((r) => r.data);
// Phase-2 detection endpoints
export const detectColumns = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/columns`).then((r) => r.data);
export const detectWizardCommodity = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/commodity`).then((r) => r.data);
export const detectDepositType = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/deposit-type`).then((r) => r.data);
export const detectLithology = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/lithology`).then((r) => r.data);
export const detectSearchStrategy = (pid, params = {}) =>
  api.get(`/projects/${pid}/wizard/detect/search-strategy`, { params }).then((r) => r.data);
export const detectBlockModel = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/block-model`).then((r) => r.data);
export const detectDensity = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/density`).then((r) => r.data);
export const detectMethod = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/estimation-method`).then((r) => r.data);
export const detectClassification = (pid) =>
  api.get(`/projects/${pid}/wizard/detect/classification`).then((r) => r.data);
// Wizard runs
export const startWizardRun = (pid, params = {}) =>
  api.post(`/projects/${pid}/wizard/runs`, { params }).then((r) => r.data);
export const listWizardRuns = (pid) =>
  api.get(`/projects/${pid}/wizard/runs`).then((r) => r.data);
export const getWizardRun = (pid, runId) =>
  api.get(`/projects/${pid}/wizard/runs/${runId}`).then((r) => r.data);
export const cancelWizardRun = (pid, runId) =>
  api.post(`/projects/${pid}/wizard/runs/${runId}/cancel`).then((r) => r.data);

export const listWizardPresets = () =>
  api.get(`/wizard/presets`).then((r) => r.data);
export const getWizardPreset = (id) =>
  api.get(`/wizard/presets/${id}`).then((r) => r.data);
export const createWizardPreset = (body) =>
  api.post(`/wizard/presets`, body).then((r) => r.data);
export const patchWizardPreset = (id, body) =>
  api.patch(`/wizard/presets/${id}`, body).then((r) => r.data);
export const deleteWizardPreset = (id) =>
  api.delete(`/wizard/presets/${id}`).then((r) => r.data);
export const generateCprPdf = (pid, body) =>
  api.post(`/projects/${pid}/wizard/cpr-pdf`, body,
             { responseType: "blob" }).then((r) => r.data);

export const getJob = (jid) => api.get(`/jobs/${jid}`).then((r) => r.data);
export const listJobs = (pid, opts = {}) => {
  const params = {};
  if (opts.limit != null)  params.limit = opts.limit;
  if (opts.offset != null) params.offset = opts.offset;
  if (opts.status)         params.status = opts.status;
  if (opts.type)           params.type = opts.type;
  if (opts.q)              params.q = opts.q;
  return api.get(`/projects/${pid}/jobs`, { params }).then((r) => r.data);
};
export const rerunJob = (pid, jid) => api.post(`/projects/${pid}/jobs/${jid}/rerun`).then((r) => r.data);
export const deleteJob = (pid, jid) => api.delete(`/projects/${pid}/jobs/${jid}`).then((r) => r.data);
// NB: Pit / Portfolio / MonteCarlo endpoints removed in Sprint P13.
export const generateAISummary = (id) => api.post(`/projects/${id}/ai_summary`).then((r) => r.data);
export const getAISummary = (id) => api.get(`/projects/${id}/ai_summary`).then((r) => r.data);

// ---- Estimation Wizard: auto-detect + orebodies + validator + multi-pit ----
// These four endpoints power the Estimation Wizard's data-driven flow.
// `autoDetectProject` returns commodity fingerprint, deposit style, column
// mappings, and parameter recommendations. `getOrebodies` runs connected-
// component clustering on the current block model; `validateEstimate` runs
// the geological QC pass; `recommendMultiPit` runs the pit optimiser per
// orebody. See backend `auto_detect.py`, `estimation_validator.py`,
// `multi_pit.py` for the definitive schema.
export const autoDetectProject = (id) =>
  api.get(`/projects/${id}/auto-detect`).then((r) => r.data);
export const getProjectValidation = (id) =>
  api.get(`/projects/${id}/validation`).then((r) => r.data);
// Sprint P13 · Safe auto-fix — applies idempotent corrections to CSV data.
export const applyValidationAutofix = (id) =>
  api.post(`/projects/${id}/validation/autofix`).then((r) => r.data);
export const listEstimationTemplates = () =>
  api.get(`/estimation/templates`).then((r) => {
    // The endpoint returns { templates: [{id,label,...}, …] } — unwrap
    // to a plain list so callers can .map() directly.
    const d = r.data;
    return Array.isArray(d) ? d : (d?.templates || []);
  });
export const getEstimationTemplate = (tid) =>
  api.get(`/estimation/templates/${tid}`).then((r) => r.data);
export const getOrebodies = (id, params = {}) =>
  api.get(`/projects/${id}/orebodies`, { params }).then((r) => r.data);
export const validateEstimate = (id) =>
  api.get(`/projects/${id}/estimate/validate`).then((r) => r.data);
// recommendMultiPit removed with Pit tabs.

// ---- Wizard preview endpoints (no side-effects) ----
// These endpoints let the wizard show live previews at each step BEFORE
// running the final estimation. See backend/routes_wizard.py.
export const compositePreview = (id, body) =>
  api.post(`/projects/${id}/wizard/composite-preview`, body).then((r) => r.data);
export const getTopCut = (id, element = null) =>
  api.get(`/projects/${id}/wizard/top-cut`, { params: element ? { element } : {} }).then((r) => r.data);
export const getCutoffRecommendations = (id, element = null) =>
  api.get(`/projects/${id}/wizard/cutoff-recommendations`, { params: element ? { element } : {} }).then((r) => r.data);
export const estimationPreview = (id, body) =>
  api.post(`/projects/${id}/wizard/estimation-preview`, body).then((r) => r.data);

// ---- Validation / Statistics / Narrative ----
export const validateProject = (pid) =>
  api.get(`/projects/${pid}/validation`).then((r) => r.data);

// ---- Sprint P32 · Density Matrix Editor (per-litho/weather/domain/ox with CP sign-off) ----
export const getDensityMatrix = (pid) =>
  api.get(`/projects/${pid}/density/matrix`).then((r) => r.data);
export const buildDensityMatrix = (pid) =>
  api.post(`/projects/${pid}/density/matrix/build`).then((r) => r.data);
export const saveDensityMatrix = (pid, body) =>
  api.post(`/projects/${pid}/density/matrix`, body).then((r) => r.data);
export const densityMatrixHistory = (pid) =>
  api.get(`/projects/${pid}/density/matrix/history`).then((r) => r.data);
export const approveDensityRow = (pid, version, body) =>
  api.post(`/projects/${pid}/density/matrix/${version}/approve-row`, body).then((r) => r.data);
export const approveDensityAll = (pid, version) =>
  api.post(`/projects/${pid}/density/matrix/${version}/approve-all`).then((r) => r.data);
export const linkDensityToModel = (pid, version, body) =>
  api.post(`/projects/${pid}/density/matrix/${version}/link-to-model`, body).then((r) => r.data);

// ---- Sprint P32 · Setup Propagation Audit ----
export const getSetupPropagation = (pid) =>
  api.get(`/projects/${pid}/setup-propagation`).then((r) => r.data);

// ---- Sprint P32 · Audit-log retention ----
export const getRetentionConfig = () =>
  api.get(`/audit-retention/config`).then((r) => r.data);
export const setRetentionConfig = (body) =>
  api.post(`/audit-retention/config`, body).then((r) => r.data);
export const purgeAuditNow = () =>
  api.post(`/audit-retention/purge`).then((r) => r.data);
export const getRetentionStats = () =>
  api.get(`/audit-retention/stats`).then((r) => r.data);

// ---- Sprint P32 · GeoTIFF ingest ----
export const probeGeotiff = (pid, file) => {
  const fd = new FormData(); fd.append("file", file);
  return api.post(`/projects/${pid}/geotiff/probe`, fd,
                   { headers: { "Content-Type": "multipart/form-data" } })
              .then((r) => r.data);
};
export const uploadGeotiff = (pid, file, opts = {}) => {
  const fd = new FormData(); fd.append("file", file);
  if (opts.reproject_to_epsg != null) fd.append("reproject_to_epsg", String(opts.reproject_to_epsg));
  if (opts.subsample != null) fd.append("subsample", String(opts.subsample));
  return api.post(`/projects/${pid}/geotiff/upload`, fd,
                   { headers: { "Content-Type": "multipart/form-data" } })
              .then((r) => r.data);
};


export const getStatistics = (pid) =>
  api.get(`/projects/${pid}/statistics`).then((r) => r.data);
export const getGeologyNarrative = (pid) =>
  api.get(`/projects/${pid}/geology-narrative`).then((r) => r.data);

// ---- Wireframes ----
export const listWireframes = (pid) => api.get(`/projects/${pid}/wireframes`).then((r) => r.data.wireframes);
export const flagWireframeDomain = (pid, wid, body) =>
  api.patch(`/projects/${pid}/wireframes/${wid}/domain`, body).then((r) => r.data);

// ---- Pit Design — REMOVED in Sprint P13 ----

// ---- Domains (saved queries) ----
export const listDomains = (pid) => api.get(`/projects/${pid}/domains`).then((r) => r.data);
export const saveDomain = (pid, body) => api.post(`/projects/${pid}/domains`, body).then((r) => r.data);
export const deleteDomain = (pid, did) => api.delete(`/projects/${pid}/domains/${did}`).then((r) => r.data);
export const promoteDomainToLibrary = (pid, did) => api.post(`/projects/${pid}/domains/${did}/promote`).then((r) => r.data);
export const listUserDomains = () => api.get(`/user/domains`).then((r) => r.data);
export const saveUserDomain = (body) => api.post(`/user/domains`, body).then((r) => r.data);
export const deleteUserDomain = (did) => api.delete(`/user/domains/${did}`).then((r) => r.data);

// ---- Job result downloads ----
export async function downloadJobSummary(pid, jid) {
  const res = await api.get(`/projects/${pid}/jobs/${jid}/summary.csv`, { responseType: "blob" });
  triggerBlobDownload(res, `job_${jid.slice(0,8)}_summary.csv`);
}
export async function downloadJobResult(pid, jid) {
  const res = await api.get(`/projects/${pid}/jobs/${jid}/result.csv`, { responseType: "blob" });
  triggerBlobDownload(res, `job_${jid.slice(0,8)}_result.csv`);
}

function triggerBlobDownload(res, fallbackName) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  let fname = fallbackName;
  const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
  const m = cd && /filename="?([^";]+)"?/i.exec(cd);
  if (m) fname = m[1];
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Report metadata (Competent Person details) ----
export const getReportMeta = (pid) => api.get(`/projects/${pid}/report-meta`).then((r) => r.data);
export const saveReportMeta = (pid, body) => api.put(`/projects/${pid}/report-meta`, body).then((r) => r.data);
export const listReportTemplates = () => api.get(`/report-templates`).then((r) => r.data);

// ---- CP Signature blocks (per project, per kind = prepared|reviewed|approved) ----
export async function uploadSignature(pid, kind, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/projects/${pid}/signatures/${kind}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
export const deleteSignature = (pid, kind) =>
  api.delete(`/projects/${pid}/signatures/${kind}`).then((r) => r.data);
export const signaturePreviewUrl = (pid, kind, cacheBust) =>
  `${BACKEND_URL}/api/projects/${pid}/signatures/${kind}${cacheBust ? `?v=${cacheBust}` : ""}`;

// ---- Per-borehole log PDFs ----
export const listBoreholes = (pid) => api.get(`/projects/${pid}/borehole-logs`).then((r) => r.data);
export const boreholePdfUrl = (pid, hid) => `${API_BASE}/projects/${pid}/borehole-logs/${encodeURIComponent(hid)}.pdf`;
export async function downloadContinuousLog(pid) {
  const res = await api.get(`/projects/${pid}/borehole-logs/all.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `Continuous_Log.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function downloadBoreholePdf(pid, hid) {
  const res = await api.get(`/projects/${pid}/borehole-logs/${encodeURIComponent(hid)}.pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `Borehole_${hid}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function downloadBoreholeDocx(pid, hid) {
  const res = await api.get(`/projects/${pid}/borehole-logs/${encodeURIComponent(hid)}.docx`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `Borehole_${hid}.docx`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function downloadContinuousLogDocx(pid) {
  const res = await api.get(`/projects/${pid}/borehole-logs/all.docx`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `Continuous_Log.docx`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function downloadComplianceBundle(pid) {
  const res = await api.get(`/projects/${pid}/compliance/bundle.zip`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `SRES_Compliance_Bundle_${pid.slice(0, 8)}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function downloadSectionSetAppendix(pid, sid, fmt = "pdf") {
  const ext = fmt === "docx" ? "docx" : "pdf";
  const res = await api.get(`/projects/${pid}/section-sets/${sid}/appendix.${ext}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = `Section_Set_${sid.slice(0, 8)}.${ext}`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ---- EPSG auto-detect ----
export const suggestEPSG = (pid) => api.get(`/projects/${pid}/epsg-suggest`).then((r) => r.data);

// ---- Exports (auth-protected via query token) ----
export const exportUrl = (id, fmt, params = {}) => {
  const token = localStorage.getItem("sres_token") || localStorage.getItem("of_token");
  const qs = new URLSearchParams({ ...params }).toString();
  return { url: `${API_BASE}/projects/${id}/export/${fmt}?${qs}`, token };
};

// Trigger a download by first fetching with auth then creating a blob URL.
export async function downloadExport(id, fmt, params = {}) {
  const res = await api.get(`/projects/${id}/export/${fmt}`, {
    params, responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  let fname = fmt.includes("report") ? `SRES.${fmt.split(".").pop()}` : `block_model.${fmt.split(".").pop()}`;
  const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
  if (cd) {
    const m = /filename="?([^";]+)"?/i.exec(cd);
    if (m) fname = m[1];
  } else {
    if (fmt.endsWith(".shp")) fname = "block_model_shp.zip";
    if (fmt.endsWith(".dwg")) fname = "block_model_dwg.zip";
  }
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  // Surface backend-emitted meta headers to the caller so the UI can react
  // (e.g. show a toast when the CPR/DOCX endpoint regenerated AI writeups).
  return {
    filename: fname,
    aiRegenerated:
      res.headers?.["x-ai-writeups-regenerated"] === "1" ||
      res.headers?.["X-AI-Writeups-Regenerated"] === "1",
  };
}

// Legacy PDF url (kept for older callers)
export const pdfReportUrl = (id) => `${API_BASE}/projects/${id}/report/pdf`;

// ---- Sprint · Report Status & QA Gate ----
export const listReportStatuses = () =>
  api.get(`/report/statuses`).then((r) => r.data);
export const reportQaGate = (pid) =>
  api.get(`/projects/${pid}/report/qa`).then((r) => r.data);
export const verifyReportDocument = (docId) =>
  api.get(`/report/verify/${encodeURIComponent(docId)}`).then((r) => r.data);

// ---- Sprint · GIS Boundary Upload ----
export const uploadProjectBoundary = (pid, file, sourceCrs = "EPSG:4326") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("source_crs", sourceCrs);
  return api.post(`/projects/${pid}/gis/boundary/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const getProjectBoundary = (pid) =>
  api.get(`/projects/${pid}/gis/boundary`).then((r) => r.data);
export const deleteProjectBoundary = (pid) =>
  api.delete(`/projects/${pid}/gis/boundary`).then((r) => r.data);

// ---- GIS Basemaps (report map set) ----
export const uploadGISBasemap = (pid, key, body) =>
  api.post(`/projects/${pid}/gis/basemaps/${key}`, body).then((r) => r.data);
export const listGISBasemaps = (pid) =>
  api.get(`/projects/${pid}/gis/basemaps`).then((r) => r.data);
export const clearGISBasemaps = (pid) =>
  api.delete(`/projects/${pid}/gis/basemaps`).then((r) => r.data);
export const gisBasemapPngUrl = (pid, key) =>
  `${API_BASE}/projects/${pid}/gis/basemaps/${key}.png`;

// ---- Sprint · Project Configuration ----
export const detectCommodity = (pid) =>
  api.get(`/projects/${pid}/config/commodity-detect`).then((r) => r.data);
export const suggestDepositTypes = (pid, commodity) =>
  api.get(`/projects/${pid}/config/deposit-types`,
           { params: commodity ? { commodity } : {} }).then((r) => r.data);
export const getLithologyTable = (pid) =>
  api.get(`/projects/${pid}/config/lithology-table`).then((r) => r.data);
export const recommendCutoffs = (pid, body = {}) =>
  api.post(`/projects/${pid}/config/cutoff-recommend`, body).then((r) => r.data);
export const listProjectConfigurations = (pid) =>
  api.get(`/projects/${pid}/configurations`).then((r) => r.data);
export const saveProjectConfiguration = (pid, body) =>
  api.post(`/projects/${pid}/configurations`, body).then((r) => r.data);
export const approveProjectConfiguration = (pid, version, body = {}) =>
  api.post(`/projects/${pid}/configurations/${version}/approve`, body).then((r) => r.data);
export const getActiveConfiguration = (pid) =>
  api.get(`/projects/${pid}/configurations/active`).then((r) => r.data);
export const getEconomicsTemplate = (pid) =>
  api.get(`/projects/${pid}/config/economics-template`).then((r) => r.data);
export const previewEconomicsSummary = (pid, body) =>
  api.post(`/projects/${pid}/config/economics-summary`, body).then((r) => r.data);
export const getCollarsWgs84 = (pid) =>
  api.get(`/projects/${pid}/gis/collars-wgs84`).then((r) => r.data);
export const runVariogramCrossValidate = (pid, body) =>
  api.post(`/projects/${pid}/variogram/cross-validate`, body).then((r) => r.data);

// ---- CRS & GIS alignment (P0 Sprint) ----------------------------------
export const getCRSRegistries = () =>
  api.get(`/crs/registries`).then((r) => r.data);
export const listCountries = () =>
  api.get(`/crs/countries`).then((r) => r.data);
export const getCountryDefaults = (name) =>
  api.get(`/crs/country-defaults`, { params: { name } }).then((r) => r.data);
export const searchEPSG = (q, limit = 25) =>
  api.get(`/crs/search`, { params: { q, limit } }).then((r) => r.data);
export const searchEPSGByCountry = (country, limit = 40) =>
  api.get(`/crs/search`, { params: { country, limit } }).then((r) => r.data);
export const recommendUTMZone = (lon, lat) =>
  api.get(`/crs/utm-recommend`, { params: { lon, lat } }).then((r) => r.data);
export const getProjectCRS = (pid) =>
  api.get(`/projects/${pid}/crs`).then((r) => r.data);
export const saveProjectCRS = (pid, body) =>
  api.post(`/projects/${pid}/crs`, body).then((r) => r.data);
export const getCRSAudit = (pid, limit = 50) =>
  api.get(`/projects/${pid}/crs/audit`, { params: { limit } }).then((r) => r.data);
export const validateUploadFile = (pid, formData) =>
  api.post(`/projects/${pid}/crs/validate-file`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
export const previewTransformation = (pid, body) =>
  api.post(`/projects/${pid}/crs/transform-preview`, body).then((r) => r.data);
// Iter108 · Arc 1960 (Kenya · Tanzania · Uganda) detection + one-click
// approve. All 3 endpoints ship in routes_crs.py; the modal wires them
// into the CRS Correction Preview flow.
export const detectArc1960 = (body) =>
  api.post(`/crs/arc1960/detect`, body).then((r) => r.data);
export const previewArc1960 = (body) =>
  api.post(`/crs/arc1960/preview`, body).then((r) => r.data);
export const approveArc1960 = (pid, body) =>
  api.post(`/projects/${pid}/crs/arc1960/apply`, body).then((r) => r.data);
// Iter108 · Imagery provider status (env vars + live-probe verdict).
export const getImageryStatus = (livProbe = false) =>
  api.get(`/admin/imagery/status`, { params: { live_probe: livProbe } }).then((r) => r.data);
export const getAlignment = (pid, params = {}) =>
  api.get(`/projects/${pid}/gis/alignment`, { params }).then((r) => r.data);
export const alignmentCsvUrl = (pid, params = {}) => {
  const q = new URLSearchParams(params).toString();
  return `${BACKEND_URL}/api/projects/${pid}/gis/alignment.csv${q ? "?" + q : ""}`;
};
export const getGISQaGate = (pid) =>
  api.get(`/projects/${pid}/gis/qa-gate`).then((r) => r.data);

// ---- Drillhole Log Reports ----
export const listDrillholeLogHoles = (pid) =>
  api.get(`/projects/${pid}/drillhole-log/holes`).then((r) => r.data);

// ---- AI Workflow (Sprint P1) — Pit AI endpoints removed in P13 ----

export const aiSectionsOrientation = (pid) =>
  api.get(`/ai/projects/${pid}/sections/orientation`).then((r) => r.data);
export const aiSectionsSpacing = (pid) =>
  api.get(`/ai/projects/${pid}/sections/spacing`).then((r) => r.data);
export const aiSectionsPlan = (pid, spacing_m = null) =>
  api.get(`/ai/projects/${pid}/sections/plan`, { params: spacing_m ? { spacing_m } : {} })
    .then((r) => r.data);
export const aiSectionsQA = (pid, plan) =>
  api.post(`/ai/projects/${pid}/sections/qa`, plan).then((r) => r.data);
export const aiSectionsInsights = (pid) =>
  api.get(`/ai/projects/${pid}/sections/insights`).then((r) => r.data);

// ---- Module 10 · Section Sets (persistent) ----
export const listSectionSets = (pid, includeArchived = false) =>
  api.get(`/projects/${pid}/section-sets/`, {
    params: includeArchived ? { include_archived: true } : {},
  }).then((r) => r.data);
export const createSectionSet = (pid, body) =>
  api.post(`/projects/${pid}/section-sets/`, body).then((r) => r.data);
export const getSectionSet = (pid, sid) =>
  api.get(`/projects/${pid}/section-sets/${sid}`).then((r) => r.data);
export const patchSectionSet = (pid, sid, patch) =>
  api.patch(`/projects/${pid}/section-sets/${sid}`, patch).then((r) => r.data);
export const deleteSectionSet = (pid, sid) =>
  api.delete(`/projects/${pid}/section-sets/${sid}`).then((r) => r.data);
export const duplicateSectionSet = (pid, sid) =>
  api.post(`/projects/${pid}/section-sets/${sid}/duplicate`).then((r) => r.data);
export const approveSectionSet = (pid, sid, body = {}) =>
  api.post(`/projects/${pid}/section-sets/${sid}/approve`, body).then((r) => r.data);
export const rejectSectionSet = (pid, sid, body = {}) =>
  api.post(`/projects/${pid}/section-sets/${sid}/reject`, body).then((r) => r.data);
export const lockSectionSet = (pid, sid, body = {}) =>
  api.post(`/projects/${pid}/section-sets/${sid}/lock`, body).then((r) => r.data);
export const unlockSectionSet = (pid, sid, body = {}) =>
  api.post(`/projects/${pid}/section-sets/${sid}/unlock`, body).then((r) => r.data);
export const sectionSetAppendixUrl = (pid, sid) =>
  `${API_BASE}/projects/${pid}/section-sets/${sid}/appendix.pdf`;
export const sectionSetAppendixDocxUrl = (pid, sid) =>
  `${API_BASE}/projects/${pid}/section-sets/${sid}/appendix.docx`;


export const aiBlockStats = (pid) =>
  api.get(`/ai/projects/${pid}/block-model/statistics`).then((r) => r.data);
export const aiBlockFieldMap = (pid) =>
  api.get(`/ai/projects/${pid}/block-model/field-map`).then((r) => r.data);
export const aiBlockRecs = (pid) =>
  api.get(`/ai/projects/${pid}/block-model/recommendations`).then((r) => r.data);
export const aiBlockZeroDiag = (pid) =>
  api.get(`/ai/projects/${pid}/block-model/zero-diagnosis`).then((r) => r.data);
export const aiBlockColorScheme = (field) =>
  api.get(`/ai/block-model/color-scheme`, { params: { field } })
    .then((r) => r.data);

export const aiOreShellGenerate = (body) =>
  api.post(`/ai/ore-shell/generate`, body).then((r) => r.data);
export const aiOreShellValidate = (body) =>
  api.post(`/ai/ore-shell/validate`, body).then((r) => r.data);
export const aiOreShellStatistics = (body) =>
  api.post(`/ai/ore-shell/statistics`, body).then((r) => r.data);

export const aiCopilotParse = (text, context = {}) =>
  api.post(`/ai/copilot/parse`, { text, context }).then((r) => r.data);
export const aiCopilotSuggestions = (module = null) =>
  api.get(`/ai/copilot/suggestions`, { params: module ? { module } : {} })
    .then((r) => r.data);

export const aiAppendixManifest = (pid) =>
  api.get(`/ai/projects/${pid}/report/appendix-manifest`).then((r) => r.data);
export const aiChaptersManifest = (pid) =>
  api.get(`/ai/projects/${pid}/report/dynamic-chapters-manifest`).then((r) => r.data);

export const listEconomicsCommodities = () =>
  api.get(`/economics/commodities`).then((r) => r.data);

// -------- Enterprise Economics v2 (Phase 1-4 upgrade) --------
export const econV2ListCurrencies = () =>
  api.get(`/economics-v2/currencies`).then((r) => r.data);
export const econV2ListCountries = () =>
  api.get(`/economics-v2/countries`).then((r) => r.data);
export const econV2GetReportingCurrency = (pid) =>
  api.get(`/economics-v2/projects/${pid}/reporting-currency`).then((r) => r.data);
export const econV2SetReportingCurrency = (pid, body) =>
  api.post(`/economics-v2/projects/${pid}/reporting-currency`, body).then((r) => r.data);
export const econV2SetCountry = (pid, body) =>
  api.put(`/economics-v2/projects/${pid}/country`, body).then((r) => r.data);
export const econV2Readiness = (pid) =>
  api.get(`/economics-v2/projects/${pid}/readiness`).then((r) => r.data);
export const econV2Validate = (pid) =>
  api.get(`/economics-v2/projects/${pid}/validation`).then((r) => r.data);
export const econV2Scale = (pid) =>
  api.get(`/economics-v2/projects/${pid}/scale`).then((r) => r.data);
export const econV2ThroughputRec = (pid, target_mine_life_years) =>
  api.get(`/economics-v2/projects/${pid}/throughput-recommendation`, {
    params: target_mine_life_years ? { target_mine_life_years } : {},
  }).then((r) => r.data);
export const econV2Benchmarks = (pid) =>
  api.get(`/economics-v2/projects/${pid}/market-benchmarks`).then((r) => r.data);
export const econV2AnalyseMarket = (pid) =>
  api.post(`/economics-v2/projects/${pid}/analyse-market`).then((r) => r.data);
// P12 · Live research for cost / country benchmarks.
export const econV2AnalyseCost = (pid, field) =>
  api.post(`/economics-v2/projects/${pid}/analyse-cost`, { field }).then((r) => r.data);
export const econV2AnalyseCountry = (pid) =>
  api.post(`/economics-v2/projects/${pid}/analyse-country`).then((r) => r.data);
// P12 · Market-snapshot mini-chart + per-project alert preference.
export const econV2MarketHistory = (pid, days = 30) =>
  api.get(`/economics-v2/projects/${pid}/market-snapshot-history`, { params: { days } }).then((r) => r.data);
export const econV2GetMarketAlerts = (pid) =>
  api.get(`/economics-v2/projects/${pid}/market-alerts`).then((r) => r.data);
export const econV2SetMarketAlerts = (pid, enabled) =>
  api.put(`/economics-v2/projects/${pid}/market-alerts`, { enabled }).then((r) => r.data);
export const econV2GenerateCapex = (pid) =>
  api.post(`/economics-v2/projects/${pid}/generate-capex`).then((r) => r.data);
export const econV2GenerateOpex = (pid) =>
  api.post(`/economics-v2/projects/${pid}/generate-opex`).then((r) => r.data);
export const econV2Live = (pid, body = {}) =>
  api.post(`/economics-v2/projects/${pid}/live`, body).then((r) => r.data);
export const econV2Sensitivity = (pid) =>
  api.post(`/economics-v2/projects/${pid}/sensitivity`).then((r) => r.data);
export const econV2ListScenarios = (pid) =>
  api.get(`/economics-v2/projects/${pid}/scenarios`).then((r) => r.data);
export const econV2CreateScenario = (pid, body) =>
  api.post(`/economics-v2/projects/${pid}/scenarios`, body).then((r) => r.data);
export const econV2ApproveScenario = (pid, body) =>
  api.post(`/economics-v2/projects/${pid}/scenarios/approve`, body).then((r) => r.data);
export const econV2Audit = (pid, limit = 100) =>
  api.get(`/economics-v2/projects/${pid}/audit`, {
    params: { limit }
  }).then((r) => r.data);
export const getEconomicsDefaults = (commodity) =>
  api.get(`/economics/defaults`, { params: { commodity } }).then((r) => r.data);
export const computeBlockEconomics = (body) =>
  api.post(`/economics/block`, body).then((r) => r.data);
export const computeBreakevenCutoff = (body) =>
  api.post(`/economics/cutoff`, body).then((r) => r.data);
export const runSensitivityAnalysis = (body) =>
  api.post(`/economics/sensitivity`, body).then((r) => r.data);

export const singleHoleLogUrl = (pid, hole_id, element = "Au") =>
  `${API_BASE}/projects/${pid}/drillhole-log/${encodeURIComponent(hole_id)}.pdf?element=${encodeURIComponent(element)}`;
// Fetch the PDF as a blob URL so the preview iframe (which can't set Authorization
// headers) can render it. Force the mime type so browsers open the PDF viewer
// inline rather than trying to download the blob. The caller MUST
// revokeObjectURL when swapping URLs.
export async function fetchHoleLogBlobUrl(pid, hole_id, element = "Au") {
  const res = await api.get(`/projects/${pid}/drillhole-log/${encodeURIComponent(hole_id)}.pdf`,
    { params: { element }, responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}
export async function generateLogBookAsync(pid, params, onProgress) {
  const start = await api.post(`/projects/${pid}/drillhole-log/bulk`, params);
  const { job_id } = start.data || {};
  if (!job_id) throw new Error("No job_id returned");
  const started = Date.now();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  let last = null;
  while (Date.now() - started < 5 * 60 * 1000) {
    const st = await api.get(`/projects/${pid}/drillhole-log/status/${job_id}`);
    last = st.data || {};
    if (onProgress) try { onProgress(last); } catch { /* progress errors non-fatal */ }
    if (last.status === "complete") break;
    if (last.status === "failed") throw new Error(last.error || "Log book failed");
    await sleep(1500);
  }
  if (!last || last.status !== "complete") throw new Error("Log book timed out");
  const dl = await api.get(`/projects/${pid}/drillhole-log/download/${job_id}`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(dl.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = last.filename || "SRES_DrillholeLogBook.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename: a.download, jobId: job_id };
}

// ── P18b · Individual borehole downloads (Feb 2026) ─────────────────
// One folder per borehole (BH001/BH001_Log.pdf + Collar/Survey/Assay/
// Geology CSVs) so a reviewer can open a single hole without unzipping
// a 200-page merged book. Complements `generateLogBookAsync` above.
export async function downloadIndividualBoreholeZip(pid, {
  element = "Au", holeIds = null, reportNo = "SRES-DHL", version = "v1",
} = {}) {
  const params = { element, report_no: reportNo, version };
  if (holeIds?.length) params.hole_ids = holeIds.join(",");
  const res = await api.get(`/projects/${pid}/drillhole-log/individual.zip`,
    { params, responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match?.[1] || `DrillholeLogs_${pid}.zip`;
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return { filename };
}
export async function downloadBoreholeDatabaseXlsx(pid) {
  const res = await api.get(`/projects/${pid}/drillhole-log/database.xlsx`,
    { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "borehole_database.xlsx";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ── P22 · Continuous drillhole log (one merged PDF, synchronous) ────
export async function downloadContinuousDrillholeLog(pid, {
  element = "Au", reportNo = "SRES-DHL", version = "v1", cutoff = null,
} = {}) {
  const params = { element, report_no: reportNo, version };
  if (cutoff != null) params.cutoff = cutoff;
  const res = await api.get(`/projects/${pid}/drillhole-log/continuous.pdf`,
    { params, responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="([^"]+)"/);
  const filename = match?.[1] || `Continuous_Drillhole_Log_${pid}.pdf`;
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return { filename };
}

// ---- Public share links (watermarked CPR PDF) ----
export const listShares  = (pid) => api.get(`/projects/${pid}/report/share`).then((r) => r.data);
export const createShare = (pid, body = {}) => api.post(`/projects/${pid}/report/share`, body).then((r) => r.data);
export const revokeShare = (pid, token) => api.delete(`/projects/${pid}/report/share/${token}`).then((r) => r.data);
export const shareStats  = (pid) => api.get(`/projects/${pid}/report/share/stats`).then((r) => r.data);
export const publicReportUrl = (token) => `${BACKEND_URL}/api/public/reports/${token}`;

// ---- Marketing (public) ----
export const submitContact  = (body) => api.post(`/marketing/contact`, body).then((r) => r.data);
export const applyForRole   = (body) => api.post(`/marketing/careers/apply`, body).then((r) => r.data);
export const listCareerRoles = () => api.get(`/marketing/careers/roles`).then((r) => r.data);

// ---- Marketing admin (superadmin only) ----
export const listContactSubmissions = (params = {}) =>
  api.get(`/admin/marketing/contact-submissions`, { params }).then((r) => r.data);
export const listJobApplications = (params = {}) =>
  api.get(`/admin/marketing/job-applications`, { params }).then((r) => r.data);
export const updateContactStatus = (id, status) =>
  api.patch(`/admin/marketing/contact-submissions/${id}`, { status }).then((r) => r.data);
export const updateApplicationStatus = (id, status) =>
  api.patch(`/admin/marketing/job-applications/${id}`, { status }).then((r) => r.data);
export const deleteContactSubmission = (id) =>
  api.delete(`/admin/marketing/contact-submissions/${id}`).then((r) => r.data);
export const deleteJobApplication = (id) =>
  api.delete(`/admin/marketing/job-applications/${id}`).then((r) => r.data);

// ---- Superadmin Console ----
export const saListUsers        = () => api.get(`/admin/users`).then((r) => r.data);
export const saUpdateUser       = (id, body) => api.patch(`/superadmin/users/${id}`, body).then((r) => r.data);
export const saDeleteUser       = (id) => api.delete(`/superadmin/users/${id}`).then((r) => r.data);
export const saBulkDeleteUsers  = (ids) => api.post(`/superadmin/users/bulk-delete`, { ids }).then((r) => r.data);
export const saResetUserPassword = (id, body = {}) => api.post(`/superadmin/users/${id}/reset-password`, body).then((r) => r.data);

export const saListCompanies    = () => api.get(`/superadmin/companies`).then((r) => r.data);
export const saRenameCompany    = (name, new_name) => api.patch(`/superadmin/companies/${encodeURIComponent(name)}`, { new_name }).then((r) => r.data);
export const saDeleteCompany    = (name) => api.delete(`/superadmin/companies/${encodeURIComponent(name)}`).then((r) => r.data);
export const saBulkDeleteCompanies = (names) => api.post(`/superadmin/companies/bulk-delete`, { names }).then((r) => r.data);
export const saResetCompanyPassword = (name, body = {}) => api.post(`/superadmin/companies/${encodeURIComponent(name)}/reset-password`, body).then((r) => r.data);

export const saListProjects     = () => api.get(`/superadmin/projects`).then((r) => r.data);
export const saDeleteProject    = (id) => api.delete(`/superadmin/projects/${id}`).then((r) => r.data);
export const saBulkDeleteProjects = (ids) => api.post(`/superadmin/projects/bulk-delete`, { ids }).then((r) => r.data);

// ---- Superadmin: seed demo project ----
export const listSeedCatalogue = () =>
  api.get(`/admin/seed/catalogue`).then((r) => r.data);
export const seedDemoProject = (slug) =>
  api.post(`/admin/seed/demo-project${slug ? "/" + slug : ""}`).then((r) => r.data);

// ---- About page CMS ----
export const getPublicAbout   = () => api.get(`/public/about`).then((r) => r.data);
export const adminGetAbout    = () => api.get(`/admin/about`).then((r) => r.data);
export const adminPutAbout    = (body) => api.put(`/admin/about`, body).then((r) => r.data);
export const aboutImageUrl    = (path) => path ? `${BACKEND_URL}/api/public/about/images/${path}` : "";
export async function adminUploadAboutImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/admin/about/upload-image`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;   // { image_path, size, content_type }
}


// ─── Sprint P3 · Tile proxy, Report captures, Estimation preflight ──
export const tileProxyUrl = (upstreamUrl) => {
  // Build the /api/gis/tile-proxy URL that streams a tile through the
  // backend with proper CORS headers. Accepts a full absolute URL.
  const base = api.defaults.baseURL || "";
  const q = encodeURIComponent(upstreamUrl);
  return `${base}/gis/tile-proxy?url=${q}`;
};

export const listCaptureModules = () =>
  api.get(`/reports/capture-modules`).then((r) => r.data);

export const listReportCaptures = (pid) =>
  api.get(`/projects/${pid}/report/captures`).then((r) => r.data);

export const createReportCapture = (pid, payload) =>
  api.post(`/projects/${pid}/report/captures`, payload).then((r) => r.data);

export const deleteReportCapture = (pid, cid) =>
  api.delete(`/projects/${pid}/report/captures/${cid}`).then((r) => r.data);

export const estimationPreflight = (pid) =>
  api.get(`/ai/projects/${pid}/estimation/preflight`).then((r) => r.data);

export const estimationOptimalParams = (pid) =>
  api.get(`/ai/projects/${pid}/estimation/optimal-params`).then((r) => r.data);

export const estimationMaterialClassification = (pid) =>
  api.get(`/ai/projects/${pid}/estimation/material-classification`)
     .then((r) => r.data);

export const estimationBufferZones = (pid) =>
  api.get(`/ai/projects/${pid}/estimation/buffer-zones`).then((r) => r.data);



/* ── Master Project Workflow (Feb-2026 enterprise standard) ──── */
export const getProjectWorkflow = (pid) =>
  api.get(`/projects/${pid}/workflow`).then((r) => r.data);

export const patchWorkflowModule = (pid, moduleKey, patch) =>
  api.put(`/projects/${pid}/workflow/modules/${moduleKey}`, patch).then((r) => r.data);

export const approveWorkflowModule = (pid, moduleKey, reason = "") =>
  api.post(`/projects/${pid}/workflow/modules/${moduleKey}/approve`,
             { reason }).then((r) => r.data);

export const reopenWorkflowModule = (pid, moduleKey, reason = "") =>
  api.post(`/projects/${pid}/workflow/modules/${moduleKey}/reopen`,
             { reason }).then((r) => r.data);


// ── P18a · Enterprise Completion (Feb 2026) ─────────────────────────
// Saved 3D views (persisted on project.saved_views[]).
export const listSavedViews = (pid) =>
  api.get(`/projects/${pid}/views`).then((r) => r.data);
export const createSavedView = (pid, body) =>
  api.post(`/projects/${pid}/views`, body).then((r) => r.data);
export const updateSavedView = (pid, viewId, body) =>
  api.patch(`/projects/${pid}/views/${viewId}`, body).then((r) => r.data);
export const deleteSavedView = (pid, viewId) =>
  api.delete(`/projects/${pid}/views/${viewId}`).then((r) => r.data);

// Grade-tonnage curve computed from the current block model.
export const getGradeTonnage = (pid, params = {}) =>
  api.get(`/projects/${pid}/grade-tonnage`, { params }).then((r) => r.data);

// Conceptual Pseudo-Pit (visualisation only).
export const computePseudoPit = (pid, body = {}) =>
  api.post(`/projects/${pid}/pseudo-pit`, body).then((r) => r.data);
export const getPseudoPit = (pid) =>
  api.get(`/projects/${pid}/pseudo-pit`).then((r) => r.data);

// Enterprise dashboard aggregate.
export const getEnterpriseDashboard = () =>
  api.get(`/enterprise/dashboard`).then((r) => r.data);

// CPR readiness gate.
export const getCPRReadiness = (pid) =>
  api.get(`/projects/${pid}/cpr/readiness`).then((r) => r.data);
export const setCPApproval = (pid, body) =>
  api.post(`/projects/${pid}/cpr/competent-person-approval`, body).then((r) => r.data);

// ── P19.3 · Complete Project Archive (one-click ZIP) ────────────────
export async function downloadProjectArchive(pid, projectName = "project") {
  const res = await api.get(`/projects/${pid}/archive.zip`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m?.[1] || `SRES_Archive_${projectName.replace(/\s+/g, "_")}.zip`;
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return { filename };
}

// ── P19.4 · Version control lifecycle ───────────────────────────────
export const listProjectVersions = (pid, params = {}) =>
  api.get(`/projects/${pid}/versions`, { params }).then((r) => r.data);
export const createProjectVersion = (pid, artifactType, artifactId, body = {}) =>
  api.post(`/projects/${pid}/versions/artifact/${artifactType}/${artifactId}`, body).then((r) => r.data);
export const transitionProjectVersion = (pid, versionId, body) =>
  api.post(`/projects/${pid}/versions/${versionId}/transition`, body).then((r) => r.data);
export const getProjectVersion = (pid, versionId) =>
  api.get(`/projects/${pid}/versions/${versionId}`).then((r) => r.data);
export const compareProjectVersions = (pid, va, vb) =>
  api.get(`/projects/${pid}/versions/compare/${va}/${vb}`).then((r) => r.data);


// ── P19.5 · Field Logging + Chain of Custody (Feb 2026) ─────────────
export const getFieldLogCodeLibrary = (pid) =>
  api.get(`/projects/${pid}/field-logs/code-library`).then((r) => r.data);
export const listFieldLogs = (pid, params = {}) =>
  api.get(`/projects/${pid}/field-logs`, { params }).then((r) => r.data);
export const getFieldLog = (pid, logId) =>
  api.get(`/projects/${pid}/field-logs/${logId}`).then((r) => r.data);
export const createFieldLog = (pid, body) =>
  api.post(`/projects/${pid}/field-logs`, body).then((r) => r.data);
export const updateFieldLog = (pid, logId, body) =>
  api.patch(`/projects/${pid}/field-logs/${logId}`, body).then((r) => r.data);
export const modifyFieldLogInterval = (pid, logId, body) =>
  api.post(`/projects/${pid}/field-logs/${logId}/intervals`, body).then((r) => r.data);
export const approveFieldLog = (pid, logId, comment) =>
  api.post(`/projects/${pid}/field-logs/${logId}/approve`,
             null, { params: { comment } }).then((r) => r.data);
export const listSamples = (pid, params = {}) =>
  api.get(`/projects/${pid}/samples`, { params }).then((r) => r.data);
export const createSample = (pid, body) =>
  api.post(`/projects/${pid}/samples`, body).then((r) => r.data);
export const transitionSample = (pid, sid, body) =>
  api.post(`/projects/${pid}/samples/${sid}/transition`, body).then((r) => r.data);
export const getSampleAudit = (pid, sid) =>
  api.get(`/projects/${pid}/samples/${sid}/audit`).then((r) => r.data);
export async function downloadSamplesXlsx(pid) {
  const res = await api.get(`/projects/${pid}/samples.xlsx`, { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "samples.xlsx";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}


// ── P19.2 · Mine Design Workspace (Feb 2026) ────────────────────────
export const getMineDesignSummary = (pid) =>
  api.get(`/projects/${pid}/mine-design/summary`).then((r) => r.data);
export const computeEngineeringPit = (pid, body = {}) =>
  api.post(`/projects/${pid}/mine-design/pit`, body).then((r) => r.data);
export const getEngineeringPit = (pid) =>
  api.get(`/projects/${pid}/mine-design/pit`).then((r) => r.data);
export const computeEngineeringUnderground = (pid, body = {}) =>
  api.post(`/projects/${pid}/mine-design/underground`, body).then((r) => r.data);
export const getEngineeringUnderground = (pid) =>
  api.get(`/projects/${pid}/mine-design/underground`).then((r) => r.data);
export async function downloadMineDesignArtifact(pid, artifact) {
  const res = await api.get(`/projects/${pid}/mine-design/${artifact}`, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = artifact.replace("/", "_");
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}


// ── P20 · Competent Person Module (Feb 2026) ────────────────────────
export const getCPSummary = (pid) =>
  api.get(`/projects/${pid}/cp/summary`).then((r) => r.data);
export const listCPProfiles = (pid) =>
  api.get(`/projects/${pid}/cp/profiles`).then((r) => r.data);
export const createCPProfile = (pid, body) =>
  api.post(`/projects/${pid}/cp/profiles`, body).then((r) => r.data);
export const updateCPProfile = (pid, profileId, body) =>
  api.patch(`/projects/${pid}/cp/profiles/${profileId}`, body).then((r) => r.data);
export const deleteCPProfile = (pid, profileId) =>
  api.delete(`/projects/${pid}/cp/profiles/${profileId}`).then((r) => r.data);
export const listCPResponsibilities = (pid) =>
  api.get(`/projects/${pid}/cp/responsibilities`).then((r) => r.data);
export const addCPResponsibility = (pid, body) =>
  api.post(`/projects/${pid}/cp/responsibilities`, body).then((r) => r.data);
export const removeCPResponsibility = (pid, rid) =>
  api.delete(`/projects/${pid}/cp/responsibilities/${rid}`).then((r) => r.data);
export const listCPSiteVisits = (pid) =>
  api.get(`/projects/${pid}/cp/site-visits`).then((r) => r.data);
export const addCPSiteVisit = (pid, body) =>
  api.post(`/projects/${pid}/cp/site-visits`, body).then((r) => r.data);
export const listCPDocuments = (pid, cpProfileId) =>
  api.get(`/projects/${pid}/cp/documents${cpProfileId ? `?cp_profile_id=${cpProfileId}` : ""}`).then((r) => r.data);
export const uploadCPDocument = (pid, body) =>
  api.post(`/projects/${pid}/cp/documents`, body).then((r) => r.data);
export const verifyCPDocument = (pid, docId) =>
  api.post(`/projects/${pid}/cp/documents/${docId}/verify`).then((r) => r.data);
export const listCPDeclarations = (pid) =>
  api.get(`/projects/${pid}/cp/declarations`).then((r) => r.data);
export const generateCPDeclaration = (pid, body) =>
  api.post(`/projects/${pid}/cp/declaration/generate`, body).then((r) => r.data);
export const getCPDeclaration = (pid, declId) =>
  api.get(`/projects/${pid}/cp/declaration/${declId}`).then((r) => r.data);
export const editCPDeclaration = (pid, declId, body) =>
  api.patch(`/projects/${pid}/cp/declaration/${declId}`, body).then((r) => r.data);
export const markCPDeclarationReady = (pid, declId) =>
  api.post(`/projects/${pid}/cp/declaration/${declId}/mark-ready`).then((r) => r.data);
export const signCPDeclaration = (pid, declId, body) =>
  api.post(`/projects/${pid}/cp/declaration/${declId}/sign`, body).then((r) => r.data);
export const insertCPDeclarationIntoCPR = (pid, declId) =>
  api.post(`/projects/${pid}/cp/declaration/${declId}/insert-into-cpr`).then((r) => r.data);
export async function downloadCPDeclarationPdf(pid, declId, version) {
  const res = await api.get(`/projects/${pid}/cp/declaration/${declId}/pdf`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `cp_declaration_v${version ?? 1}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ── P0.1 · Compliance Audit ─────────────────────────────────────────
export const getComplianceAudit = (pid) =>
  api.get(`/projects/${pid}/compliance/audit`).then((r) => r.data);


// ── P23 · Density Telemetry (P0.1) ──────────────────────────────────
export const getDensityTelemetry = (pid) =>
  api.get(`/projects/${pid}/density/telemetry`).then((r) => r.data);


// ── P23 · Advanced Drillhole Analytics (P1.1–P1.6) ─────────────────
export const getAnalyticsDataSelection = (pid) =>
  api.get(`/projects/${pid}/analytics/data-selection`).then((r) => r.data);
export const analyticsStatistics = (pid, body) =>
  api.post(`/projects/${pid}/analytics/statistics`, body).then((r) => r.data);
export const analyticsHistogram = (pid, body) =>
  api.post(`/projects/${pid}/analytics/histogram`, body).then((r) => r.data);
export const analyticsLogProbability = (pid, body) =>
  api.post(`/projects/${pid}/analytics/log-probability`, body).then((r) => r.data);
export const analyticsBoxPlot = (pid, body) =>
  api.post(`/projects/${pid}/analytics/box-plot`, body).then((r) => r.data);
export const analyticsContactAnalysis = (pid, body) =>
  api.post(`/projects/${pid}/analytics/contact-analysis`, body).then((r) => r.data);
export const analyticsMetalAtRisk = (pid, body) =>
  api.post(`/projects/${pid}/analytics/metal-at-risk`, body).then((r) => r.data);
export async function analyticsExport(pid, body, filename) {
  const res = await api.post(`/projects/${pid}/analytics/export`, body,
    { responseType: "blob" });
  const blob = new Blob([res.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename || `analytics_${body.kind}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}


// ── P21.2 · Multi-cutoff grade shells ───────────────────────────────
export const generateGradeShellBatch = (pid, body) =>
  api.post(`/projects/${pid}/geo-model/grade-shell-batch`, body).then((r) => r.data);


// ── P21 · Professional Geological Modelling Engine ──────────────────
export const getGeoModelSummary = (pid) =>
  api.get(`/projects/${pid}/geo-model/summary`).then((r) => r.data);
export const getGeoLithologyCodes = (pid) =>
  api.get(`/projects/${pid}/geo-model/lithology-codes`).then((r) => r.data);
export const generateGradeShell = (pid, body) =>
  api.post(`/projects/${pid}/geo-model/grade-shell`, body).then((r) => r.data);
export const generateLithologySolid = (pid, body) =>
  api.post(`/projects/${pid}/geo-model/lithology-solid`, body).then((r) => r.data);
export const generateTopography = (pid, body = {}) =>
  api.post(`/projects/${pid}/geo-model/topography`, body).then((r) => r.data);
export const getGeoAssayDiscs = (pid) =>
  api.get(`/projects/${pid}/geo-model/assay-discs`).then((r) => r.data);
export const getGeoModel = (pid, modelId) =>
  api.get(`/projects/${pid}/geo-model/${modelId}`).then((r) => r.data);
export const deleteGeoModel = (pid, modelId) =>
  api.delete(`/projects/${pid}/geo-model/${modelId}`).then((r) => r.data);
export async function downloadGeoModelObj(pid, modelId, name = "geo-model") {
  const res = await api.get(`/projects/${pid}/geo-model/${modelId}/obj`, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${name}.obj`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

